
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { PropBot } from '@/types/prop-bots';
import { CheckCircle2, Star, Zap, Shield, Target, TrendingUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';

interface EnhancedPropBotFeaturesProps {
  bot: PropBot;
  features?: string[];
  isLoading?: boolean;
}

export const EnhancedPropBotFeatures = ({ 
  bot, 
  features: propFeatures, 
  isLoading 
}: EnhancedPropBotFeaturesProps) => {
  const { t } = useTranslation();
  
  // Robust extraction of propDetails - handle both array and object formats
  const propDetails = Array.isArray(bot.propDetails) && bot.propDetails.length > 0 
    ? bot.propDetails[0] 
    : bot.propDetails;
    
  const features = propFeatures || propDetails?.features || [];
  const isMobile = useIsMobile();

  // Icon rotation for features
  const featureIcons = [Star, Zap, Shield, Target, TrendingUp, CheckCircle2];

  return (
    <Card data-tour="prop-bot-features" className="relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 via-background to-blue-500/5" />
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-primary/10 to-transparent rounded-full blur-2xl" />
      
      <CardHeader className={`${isMobile ? "p-4" : "p-6"} pb-4 relative`}>
        <CardTitle className={`${isMobile ? "text-lg" : "text-xl"} flex items-center gap-2`}>
          <div className="w-1 h-6 bg-gradient-to-b from-green-500 to-blue-500 rounded-full" />
          {t('features')}
        </CardTitle>
      </CardHeader>
      
      <CardContent className={`${isMobile ? "px-4 pb-4" : "px-6 pb-6"} relative`}>
        {features.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto mb-4 bg-muted/50 rounded-full flex items-center justify-center">
              <Star className="h-8 w-8 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground">{t('no_features_available')}</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {features.map((feature, index) => {
              const IconComponent = featureIcons[index % featureIcons.length];
              
              return (
                <div 
                  key={index} 
                  className="group flex items-start gap-3 p-3 rounded-xl border border-border/50 bg-gradient-to-r from-background via-muted/20 to-background transition-all duration-300 hover:shadow-md hover:border-border hover:-translate-y-0.5 animate-fade-in"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  {/* Animated icon */}
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-green-500/20 to-blue-500/20 rounded-full blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      <div className="relative w-8 h-8 bg-gradient-to-br from-green-500/20 to-green-500/10 rounded-full flex items-center justify-center border border-green-500/20 transition-all duration-300 group-hover:scale-110 group-hover:border-green-500/40">
                        <IconComponent className={`${isMobile ? "h-4 w-4" : "h-4 w-4"} text-green-500 transition-transform duration-300 group-hover:rotate-3`} />
                      </div>
                    </div>
                  </div>
                  
                  {/* Feature text */}
                  <div className="flex-1 min-w-0">
                    <span className={`${isMobile ? "text-sm" : "text-base"} font-medium leading-relaxed transition-colors duration-300 group-hover:text-foreground`}>
                      {feature}
                    </span>
                  </div>
                  
                  {/* Hover indicator */}
                  <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-2 h-2 bg-gradient-to-br from-green-500 to-blue-500 rounded-full" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {/* Feature count indicator */}
        {features.length > 0 && (
          <div className="mt-4 pt-4 border-t border-border/30">
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <span>{features.length} {t('features_available')}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
